import type { ReactNode } from "react";
import type { PeriodStats } from "../../api/types";
import { t } from "../../i18n";
import { formatEuro, type Cents } from "../../lib/money";
import { PAYMENT_ORDER, VAT_LETTERS, vatWithPlatforms } from "./reports";

function Row({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <div>
      <span>{label}</span>
      <b className="num">{value}</b>
      {detail && <small className="note">{detail}</small>}
    </div>
  );
}

interface Props {
  stats: PeriodStats;
  /**
   * The Z page, live with what is typed for the platforms above: what was paid online and at the door, each
   * on its own line under the till's payment methods (Furkan, 13/10/2026: "apart online betaald en online
   * cash"), and their revenue at once in the 6% of the BTW box ("onmiddelijk in 6%").
   */
  platforms?: { revenueCents: Cents; onlineCents: Cents; cashCents: Cents };
}

/** The four boxes of the prototype: Omzet, Betaalwijzen, BTW and Controle. */
export function StatsBoxes({ stats, platforms }: Props) {
  const text = t.reports;
  const { orders } = stats;
  const free = stats.free.byStaff.map((person) => `${person.name} ${formatEuro(person.cents)}`).join(" · ");
  const cancelled = stats.cancelled.orders.map((order) => text.cancelledBy(order.code, order.by ?? "?")).join(" · ");
  // Controle has no "Betaalwijze gewijzigd" and no "Bewerkt na bestelling" (Furkan, 13/10/2026: "deze 2 details
  // er nooit meer in, nooit meer weergeven")
  // only what has something on it (Furkan, 13/10/2026: "als er een waarde 0 is, dan moet dat niet op het
  // scherm, vanaf het moment dat er iets is mag dat in de lijst, dus geen overbodige info")
  const payments: { label: string; cents: Cents }[] = [
    ...PAYMENT_ORDER.map((method) => ({ label: t.paymentMethods[method], cents: stats.payments[method] })),
    ...(platforms
      ? [
          { label: text.platformOnline, cents: platforms.onlineCents },
          { label: text.platformCash, cents: platforms.cashCents },
        ]
      : []),
  ].filter((line) => line.cents !== 0);
  const checks: { label: string; value: ReactNode; detail?: string }[] = [
    ...(stats.discountCents !== 0 ? [{ label: text.discounts, value: formatEuro(stats.discountCents) }] : []),
    ...(stats.free.totalCents !== 0
      ? [{ label: text.free, value: formatEuro(stats.free.totalCents), detail: free }]
      : []),
    ...(stats.cancelled.count !== 0
      ? [
          {
            label: text.cancelled,
            value: text.countAmount(stats.cancelled.count, formatEuro(stats.cancelled.totalCents)),
            detail: cancelled,
          },
        ]
      : []),
  ];

  return (
    <div className="rgrid">
      <section className="rbox" aria-label={text.boxes.revenue}>
        <h3>{text.boxes.revenue}</h3>
        <div className="big num">{formatEuro(orders.revenueCents)}</div>
        {orders.count === 0 ? (
          <p className="note rnone">{text.none.orders}</p>
        ) : (
          <div className="kvs">
            <Row label={text.orders} value={orders.count} />
            <Row label={text.average} value={formatEuro(Math.round(orders.revenueCents / orders.count))} />
            {(["takeaway", "delivery", "table"] as const)
              .filter((channel) => stats.byChannel[channel] !== 0)
              .map((channel) => (
                <Row key={channel} label={text.channels[channel]} value={formatEuro(stats.byChannel[channel])} />
              ))}
          </div>
        )}
      </section>
      <section className="rbox" aria-label={text.boxes.payments}>
        <h3>{text.boxes.payments}</h3>
        {payments.length === 0 ? (
          <p className="note rnone">{text.none.payments}</p>
        ) : (
          <div className="kvs">
            {payments.map((line) => (
              <Row key={line.label} label={line.label} value={formatEuro(line.cents)} />
            ))}
          </div>
        )}
      </section>
      <section className="rbox" aria-label={text.boxes.vat}>
        <h3>{text.boxes.vat}</h3>
        <table className="vtab">
          <thead>
            <tr>
              <th>{text.vatColumns.rate}</th>
              <th className="r">{text.vatColumns.base}</th>
              <th className="r">{text.vatColumns.vat}</th>
              <th className="r">{text.vatColumns.total}</th>
            </tr>
          </thead>
          <tbody>
            {(platforms ? vatWithPlatforms(stats, platforms.revenueCents) : stats.vat).map((vat) => (
              <tr key={vat.rate}>
                <td>
                  {VAT_LETTERS[vat.rate]} {vat.rate}%
                </td>
                <td className="r num">{formatEuro(vat.baseCents)}</td>
                <td className="r num">{formatEuro(vat.vatCents)}</td>
                <td className="r num">{formatEuro(vat.grossCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="rbox" aria-label={text.boxes.checks}>
        <h3>{text.boxes.checks}</h3>
        {checks.length === 0 ? (
          <p className="note rnone">{text.none.checks}</p>
        ) : (
          <div className="kvs">
            {checks.map((line) => (
              <Row key={line.label} {...line} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
